import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { usd } from "@/lib/training";

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 border-t border-slate-200">
      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 shrink-0">{label}</span>
      <span data-testid={testid} className="text-sm font-semibold text-[#0B0F19] text-right">{value}</span>
    </div>
  );
}

export function CategoryCard({ c }) {
  const pricing = c.formats.length
    ? c.formats.map((f) => `${f.format === "trial" ? "Trial" : f.format === "dropin" ? "Drop-In" : "4-Week"} ${f.max_price > f.min_price ? "from " : ""}${usd(f.min_price)}`).join(" · ")
    : "Pricing announced soon";
  return (
    <Link to={`/training/${c.key}`} data-testid={`category-card-${c.key}`}
      className="group flex flex-col h-full rounded-3xl border border-slate-200 bg-white p-6 md:p-7 transition-[border-color,transform,box-shadow] duration-200 hover:border-[#0B0F19] hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-20px_rgba(11,15,25,0.35)]">
      <h3 className="text-2xl md:text-[28px] font-extrabold tracking-tighter uppercase text-[#0B0F19] leading-none">{c.name}</h3>
      <p className="mt-3 text-sm font-medium text-slate-500 leading-relaxed">{c.short_description}</p>
      <div className="mt-6 flex-1">
        <Row label="Ages" value={c.age_groups.join(" · ") || "—"} testid={`category-ages-${c.key}`} />
        <Row label="When" value={c.schedule || "Schedule coming soon"} testid={`category-schedule-${c.key}`} />
        <Row label="Formats" value={c.formats.map((f) => f.label).join(" · ") || "Coming soon"} testid={`category-formats-${c.key}`} />
        <Row label="Price" value={pricing} testid={`category-price-${c.key}`} />
      </div>
      <span data-testid={`category-cta-${c.key}`} className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#0B0F19] group-hover:bg-[#2563EB] text-white text-sm font-bold px-6 py-3.5 transition-colors">
        View Training Options <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

export default function CategoryCards({ categories, loading }) {
  return (
    <section id="training" data-testid="category-section" className="bg-white py-16 md:py-24 border-t border-slate-100 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-5 md:px-16">
        <p className="text-sm font-bold tracking-[0.25em] uppercase text-[#2563EB]">Rep Lab Training</p>
        <h2 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tighter uppercase text-[#0B0F19] leading-[0.95]">Choose your training.</h2>
        <p className="mt-4 max-w-xl text-base font-medium text-slate-500">Pick a skill, then choose your athlete's age group, a format, and a date.</p>
        {loading ? (
          <div className="mt-12 flex justify-center"><Loader2 size={28} className="animate-spin text-slate-300" /></div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
            {categories.map((c) => <CategoryCard key={c.key} c={c} />)}
          </div>
        )}
        {!loading && (
          <Link to="/lessons" data-testid="private-lessons-link" className="group mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-3xl border border-slate-200 px-6 md:px-7 py-5 hover:border-[#0B0F19] transition-colors">
            <span><span className="block text-lg font-extrabold uppercase tracking-tight text-[#0B0F19]">Private Lessons</span><span className="text-sm font-medium text-slate-500">One-on-one hitting or pitching. Pick a time that works for you.</span></span>
            <span className="inline-flex items-center gap-2 text-sm font-bold text-[#0B0F19] group-hover:text-[#2563EB]">Book a Lesson <ArrowRight size={16} /></span>
          </Link>
        )}
      </div>
    </section>
  );
}
